/**
 * PATH: packages/ui/src/components/sidebar/SidebarFooter.tsx
 * PURPOSE: Footer with Quotech branding, sign out, and collapse toggle
 */

import { ChevronLeft, ChevronRight, LogOut } from 'lucide-react'
import { cn } from '../../lib/utils'
import type { User } from '../types'

interface SidebarFooterProps {
  collapsed: boolean
  user?: User | null
  onLogout?: () => void
  onCollapsedChange?: (collapsed: boolean) => void
}

export function SidebarFooter({ collapsed, user, onLogout, onCollapsedChange }: SidebarFooterProps) {
  const handleLogout = () => {
    if (onLogout) {
      onLogout()
      return
    }
    // Default logout: clear all auth state then redirect to main dashboard
    if (typeof document !== 'undefined') {
      const cookieDomains = [window.location.hostname, '.quotech.ai']
      for (const domain of cookieDomains) {
        document.cookie = `quotech_auth=; Path=/; Domain=${domain}; Max-Age=0`
        document.cookie = `quotech_refresh=; Path=/; Domain=${domain}; Max-Age=0`
      }
    }
    if (typeof localStorage !== 'undefined') {
      const keys = [
        'quotech-auth', 'UserData', 'IdToken', 'refreshToken', 'org_id',
        'CurrentTenantId', 'CurrentTenantName', 'org_name', 'ProfileData',
        'accessTokennew', 'quotech_sso_email',
      ]
      for (const k of keys) localStorage.removeItem(k)
    }
    window.location.href = 'https://dashboard.quotech.ai/login?logout=1'
  }

  return (
    <>
      {/* Footer: Quotech Branding + Sign out */}
      <div className="border-t border-border p-2">
        <div className={cn(
          'flex pt-2 border-t border-border',
          collapsed ? 'flex-col items-center gap-2' : 'items-center gap-2 px-1'
        )}>
          <a href="https://dashboard.quotech.ai"
            className={cn(
              'flex items-center justify-center rounded-lg bg-gradient-to-br from-slate-700 to-slate-800 flex-shrink-0 hover:opacity-90 transition-opacity',
              collapsed ? 'w-8 h-8' : 'w-6 h-6'
            )} title="Quotech Home">
            <span className={cn('text-white font-bold', collapsed ? 'text-sm' : 'text-xs')}>Q</span>
          </a>
          {!collapsed && (
            <a href="https://dashboard.quotech.ai/dashboard"
              className="flex-1 min-w-0 hover:opacity-80 transition-opacity">
              <p className="text-[11px] text-muted-foreground">
                Powered by <span className="font-medium text-muted-foreground hover:text-teal-500">Quotech</span>
              </p>
            </a>
          )}
          {(user || onLogout) && (
            <button onClick={handleLogout} title="Sign out" aria-label="Sign out"
              className={cn(
                'p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors touch-manipulation',
                collapsed && 'w-8 h-8 flex items-center justify-center'
              )}>
              <LogOut className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Collapse Toggle */}
      {onCollapsedChange && (
        <button
          type="button"
          onClick={() => onCollapsedChange(!collapsed)}
          className="absolute -right-3 top-16 w-6 h-6 bg-card border border-border rounded-full flex items-center justify-center shadow-sm hover:bg-muted transition-colors z-10 touch-manipulation focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-500"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!collapsed}
          aria-controls="quotech-primary-nav">
          {collapsed ? <ChevronRight className="w-3 h-3 text-slate-500" aria-hidden="true" /> : <ChevronLeft className="w-3 h-3 text-slate-500" aria-hidden="true" />}
        </button>
      )}
    </>
  )
}
